import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LanguageIcon from '@mui/icons-material/Language';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StarIcon from '@mui/icons-material/Star';
import type { DashboardScopeT, DashboardT } from 'business/definitions';
import { DASHBOARD_SCOPE } from 'business/definitions';
import _ from 'lodash';
import type { ScheduledDashboardEmail } from 'modules/decision/components/DashboardSendModal/types';
import APICall from 'store/APIClient';

function getDashboardScope(dashboard: DashboardT): DashboardScopeT {
    if (dashboard.isTemplate) return DASHBOARD_SCOPE.TEMPLATE;
    if (dashboard.isTutorial) return DASHBOARD_SCOPE.TUTORIAL;
    if (dashboard.orgSharing) {
        if (dashboard.orgSharing.isPublic) return DASHBOARD_SCOPE.PUBLIC;
        if (dashboard.orgSharing.isRestricted)
            return DASHBOARD_SCOPE.RESTRICTED;
        if (
            dashboard.usersSharing.length > 0 ||
            dashboard.groupsSharing.length > 0
        )
            return DASHBOARD_SCOPE.SHARED;
    }
    return DASHBOARD_SCOPE.PRIVATE;
}
/**
 * Sort dashboards according to their scope. We display a dashboard at the higher scope it is shared.
 * For instance dashboard public (read only) with edit access at restricted scope will be
 * displayed in the 'public' column.
 */
export function splitDashboardsByScope(
    dashboards: DashboardT[],
): Partial<Record<DashboardScopeT, DashboardT[]>> {
    return _.groupBy(dashboards, getDashboardScope);
}

/**
 * Centralized scope icon objects.
 * @param {string} scope value of the scope
 */
export function getIconFromScope(scope: DashboardScopeT): JSX.Element {
    switch (scope) {
        case DASHBOARD_SCOPE.RECENT:
            return <AccessTimeFilledIcon />;
        case DASHBOARD_SCOPE.FAVORITE:
            return <StarIcon />;
        case DASHBOARD_SCOPE.PRIVATE:
            return <LockIcon />;
        case DASHBOARD_SCOPE.SHARED:
            return <PeopleIcon />;
        case DASHBOARD_SCOPE.RESTRICTED:
            return <AccountBalanceIcon />;
        case DASHBOARD_SCOPE.PUBLIC:
            return <LanguageIcon />;
        case DASHBOARD_SCOPE.TEMPLATE:
            return <ScreenShareIcon />;
        default:
            return <></>;
    }
}

export function sendScheduledDashboardEmailNow(
    dashboardKey: number,
    scheduledEmail: ScheduledDashboardEmail,
) {
    // we don't want to wait for this mail to be sent before sending the config, so we let it
    // run asynchronously
    APICall({
        endpoint: `explore/dashboard_emails/${dashboardKey}`,
        method: 'POST',
        body: { dashboardKey, ...scheduledEmail },
    });
}

/**
 * Check if we are currently on a public dashboard page, based on pathname.
 * @returns boolean
 */
export const isCurrentlyOnPublicDashboard = (): boolean =>
    window.location.pathname.includes('/public/dashboard/');

/**
 * Fills additional dashboard categories Recent and Favorite for given dashboard list.
 */
export const addRecentAndFavoriteScopesContent = (
    scopedDashboards: Partial<Record<DashboardScopeT, DashboardT[]>>,
    recentDashboards: DashboardT[] | undefined,
    otherDashboards: DashboardT[],
) => {
    const recentKeys = new Set(recentDashboards?.map(({ key }) => key));
    const filteredRecent = otherDashboards.filter(({ key }) =>
        recentKeys.has(key),
    );
    const filteredFavorite = otherDashboards.filter(
        (dashboard) => dashboard.isUserFavourite,
    );

    scopedDashboards.recent =
        filteredRecent.length !== 0 ? filteredRecent : undefined;
    scopedDashboards.favorite =
        filteredFavorite.length !== 0 ? filteredFavorite : undefined;
};
